import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import { TechnicalParametersRuntype } from '../part/partBackendTypes';
import { UsedInResponseRuntype } from '../rfq';
import { RfQContextQueryParamsRuntype } from '../rfqContext';
import {
    ComponentsAggregationsResponseDTORuntype,
    ComponentsHealthSummaryRuntype,
    ComponentsLastImportRuntype,
    ComponentSpecificationRuntype,
    ComponentsSearchResponseDTORuntype,
    FindIpnsQueryRuntype,
    FindIpnsResponseRuntype,
    IpnFormAndFitFormPatchValuesRuntype,
    OtsComponentFullRuntype,
} from './internalPartNumberBackendTypes';

// Split into groups with an explicit type: the inferred one exceeds what TypeScript can serialize.

const componentEndpoints = {
    'GET /components/health-summary': endpoint({
        description: 'Returns health summary data for components',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: ComponentsHealthSummaryRuntype }),
    }),
    'GET /components/last-import': endpoint({
        description:
            'Freshest last_imported_at across the whole component library (Postgres data freshness) and as reflected by the search index (Elasticsearch).',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: ComponentsLastImportRuntype,
    }),
    'POST /components/search': endpoint({
        description:
            'Searches IPNs via elasticsearch, expected to be faster than GET /ipns/search, returns an array of OTS and Custom components',
        pathParams: z.undefined(),
        queryParams: z.undefined(),

        requestBody: z
            .object({
                filters: z.array(
                    z.object({
                        field: z.string(),
                        operator: z.string(),
                        parameter: z.unknown(),
                    }),
                ),
                page_params: z.number().nullable().optional(),
                global_filter: z.string(),
                page_size: z.number().optional(),
                prioritise_preferred_ipns: z.boolean().optional(),
            })
            .and(RfQContextQueryParamsRuntype),
        responseBody: ComponentsSearchResponseDTORuntype,
    }),
    'POST /components/aggregations': endpoint({
        description:
            'Runs the same filter logic as POST /components/search but returns only the aggregations (no document hits).',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z
            .object({
                filters: z.array(
                    z.object({
                        field: z.string(),
                        operator: z.string(),
                        parameter: z.unknown(),
                    }),
                ),
                global_filter: z.string(),
                prioritise_preferred_ipns: z.boolean().optional(),
            })
            .and(RfQContextQueryParamsRuntype),
        responseBody: ComponentsAggregationsResponseDTORuntype,
    }),
    'POST /components/demand-tree': endpoint({
        description: 'Returns off-the-shelf components with at least one linked off-the-shelf part for demand trees',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            page: z.number(),
            page_size: z.number(),
            manufacturer_ids: z.array(z.string()).optional(),
        }),
        responseBody: z.object({
            items: z.array(OtsComponentFullRuntype),
        }),
    }),

    'POST /components/import': endpoint({
        description: 'Imports components',
        pathParams: z.undefined(),
        queryParams: z.object({
            live: z.boolean().optional(),
        }),
        requestBody: z.array(
            z.object({
                component: z.object({
                    type: z.literal('OffTheShelf'),
                    data: z.object({
                        parts: z.array(
                            z.object({
                                manufacturer: z.string().optional(),
                                mpn: z.string().optional(),
                            }),
                        ),
                    }),
                }),
                customer_part_numbers: z.array(z.unknown()),
                description: z.string(),
                category: z.string().nullable().optional(),
                internal_part_number: z.object({
                    revision: z.string().nullable().optional(),
                    value: z.string(),
                }),
                is_preferred: z.boolean(),
                is_restricted_to_customers: z.boolean(),
            }),
        ),
        responseBody: z.unknown(),
    }),
};

const ipnSearchEndpoints = {
    'POST /ipns/search/linked-parts/bulk': endpoint({
        description: 'Returns an array of IpnWithMatchesFullPartRuntype that have the provided parts as linked parts',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            generic_parts: z.array(z.string()),
            off_the_shelf_parts: z.array(z.string()),
            rfq_context: z.literal('WithinRfQ'),
            rfq_id: z.string(),
        }),
        responseBody: z.object({
            items: z.array(OtsComponentFullRuntype),
        }),
    }),
    'POST /ipns/search/suggested-parts/bulk': endpoint({
        description:
            'Returns an array of IpnWithMatchesFullPartRuntype that have the provided parts in their suggestions',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            generic_parts: z.array(z.string()),
            incomplete_generic_parts: z.array(TechnicalParametersRuntype),
            rfq_context: z.literal('WithinRfQ'),
            rfq_id: z.string(),
        }),
        responseBody: z.object({
            items: z.array(OtsComponentFullRuntype),
        }),
    }),
    'POST /ipns/search/generics/bulk': endpoint({
        description:
            'Returns an array of IpnWithMatchesFullPartRuntype with matching component spec for each generic part id',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            generic_parts: z.array(z.string()),
            rfq: z.string(),
            rfq_context: z.union([z.literal('WithinRfQ'), z.literal('OutsideRfQ')]),
            rfq_id: z.string().optional(),
        }),
        responseBody: z.object({
            items: z.record(z.string(), z.array(OtsComponentFullRuntype)),
        }),
    }),
    'POST /ipns/find': endpoint({
        description: 'Resolves IPN values, optionally pinned to a revision, to components or assemblies in bulk',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({ queries: z.array(FindIpnsQueryRuntype) }),
        responseBody: FindIpnsResponseRuntype,
    }),
};

const ipnUpdateAndAssemblyEndpoints = {
    'PATCH /ipns/:id/form-and-fit': endpoint({
        description: 'Updates edited form-and-fit for this ipn',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: IpnFormAndFitFormPatchValuesRuntype,
        responseBody: z.null(),
        invalidates: [
            'POST /ipns/bulk',
            'POST /parts/off-the-shelf/bulk',
            'GET /assemblies/:assemblyId/descendants',
            'POST /parts/availability',
            'POST /components/bulk',
            'POST /components/custom/bulk',
        ],
    }),
    'PATCH /ipns/:id': endpoint({
        description: 'Updates edited component specification for this ipn',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: ComponentSpecificationRuntype,
        responseBody: z.undefined().or(z.null()),
        invalidates: [
            'POST /components/search',
            'POST /components/bulk',
            'POST /ipns/search/linked-parts/bulk',
            'POST /ipns/bulk',
        ],
    }),
    'POST /ipns/assemblies/bulk': endpoint({
        description: 'Returns an array of assemblies in which the specified ipn have been used',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            ids: z.array(z.string()),
            rfq_context: z.union([z.literal('WithinRfQ'), z.literal('OutsideRfQ')]),
            rfq_id: z.string().optional(),
        }),
        responseBody: z.object({
            data: UsedInResponseRuntype,
        }),
    }),
};

type ComponentEndpoints = typeof componentEndpoints;
type IpnSearchEndpoints = typeof ipnSearchEndpoints;
type IpnUpdateAndAssemblyEndpoints = typeof ipnUpdateAndAssemblyEndpoints;

interface InternalPartNumberEndpoints extends ComponentEndpoints, IpnSearchEndpoints, IpnUpdateAndAssemblyEndpoints {}

export const internalPartNumberEndpoints: InternalPartNumberEndpoints = {
    ...componentEndpoints,
    ...ipnSearchEndpoints,
    ...ipnUpdateAndAssemblyEndpoints,
};
